import { mediaQueries } from "@/ui/theme/mediaQueries"
import styled from "@emotion/styled"
import { FilledInput, FormControl, InputLabel } from "@mui/material"
import { FormFieldDto } from "api"
import { useLocale } from "next-intl"
import { Controller } from "react-hook-form"
import { StyledHelperText } from "../StyledComponents"
import { getAutocompleteAttribute } from "../utils/autocompleteUtils"
import { FormFieldSettingsProps } from "../utils/interfaces"

const StyledFilledInput = styled(FilledInput)`
  height: auto;
`
const StyledInputLabel = styled(InputLabel)`
  top: 7px;
  ${mediaQueries.desktop} {
    top: 3px;
  }
`

interface Props {
  field: FormFieldDto
  control: any
  error: any
  isSubmitted: boolean
  fieldName?: string // Add optional fieldName prop
}

const TextField = ({
  field,
  control,
  error,
  isSubmitted,
  fieldName,
}: Props) => {
  const settings = field.settings as unknown as FormFieldSettingsProps
  const locale = useLocale()
  const actualFieldName = fieldName || field.id // Use fieldName if provided, fallback to field.id

  return (
    <FormControl fullWidth variant="filled">
      <StyledInputLabel htmlFor={actualFieldName}>
        {field.helpText}
        {field.required && " *"}
      </StyledInputLabel>
      <Controller
        name={actualFieldName}
        control={control}
        render={({ field: { onChange, value } }) => {
          // Ensure value is always a string for text/textarea, or a string/number for number
          let inputValue: string | number = value ?? ""
          if (
            settings.fieldType === "number" &&
            (value === undefined || value === null || value === "")
          ) {
            inputValue = ""
          } else if (
            settings.fieldType === "number" &&
            typeof value === "number"
          ) {
            inputValue = value
          } else if (
            settings.fieldType === "number" &&
            typeof value === "string"
          ) {
            inputValue = value
          } else if (typeof value !== "string") {
            inputValue = String(value ?? "")
          }

          // Stable multiline/rows/type
          const isTextarea: boolean =
            settings.fieldType === "textarea" ||
            !!(settings.numberOfRows && parseInt(settings.numberOfRows) > 1)
          const isNumber = settings.fieldType === "number"
          const isTel = settings.fieldType === "tel"
          const inputType = isTel ? "tel" : "text"

          // Build inputProps without undefined values
          const inputProps: Record<string, string | boolean> = {
            autoComplete:
              getAutocompleteAttribute(
                field.alias || "",
                settings.fieldType,
                settings,
              ) || "off",
            "aria-invalid": !!error ? "true" : "false",
            "aria-describedby": `${actualFieldName}-helper-text`,
          }
          if (isTel && locale === "he") inputProps.dir = "rtl"
          if (isNumber) inputProps.inputMode = "numeric"
          return (
            <StyledFilledInput
              key={settings.fieldType}
              id={actualFieldName || ""}
              onChange={onChange}
              value={inputValue}
              error={!!error}
              multiline={isTextarea}
              rows={isTextarea ? 4 : 1}
              type={inputType}
              inputProps={inputProps}
              sx={{
                borderRadius: "4px",
              }}
            />
          )
        }}
      />
      <StyledHelperText id={`${actualFieldName}-helper-text`} status={!error}>
        {isSubmitted && error && <span>{error?.message as string}</span>}
      </StyledHelperText>
    </FormControl>
  )
}

export default TextField
